import { rewriteUrl } from '@fullstory/url-rewriter';
import { uaIsChrome } from '@fullstory/useragent';
import { loadEvent } from '@fullstory/util/promise';

export type PreloadPriority =
  // Fetch the resource in the background at low priority. Note that low
  // priority resources may never get fetched at all since higher-priority
  // resources will starve them.
  | 'low'
  // Fetch the resource ASAP, even if it would block other requests.
  | 'high';

export class ResourcePreloader {
  private readonly _supportsPrefetch: boolean;
  private readonly _supportsPreload: boolean;
  private readonly _preloadedHrefs = new Set<string>();
  private _queuedPreloadsDone = Promise.resolve();

  constructor(private _document: Document = document) {
    const link = this._document.createElement('link');
    this._supportsPrefetch = link.relList.supports('prefetch');
    this._supportsPreload = link.relList.supports('preload');
  }

  /**
   * Start preloading a resource with the given priority. Resources that have
   * already been preloaded will be ignored, so it's safe to call this more than
   * once for the same resource.
   */
  preloadWithPriority(
    priority: PreloadPriority,
    href: string,
    destination: RequestDestination,
  ): void {
    if (this._preloadedHrefs.has(href)) {
      return; // Nothing to do.
    }
    this._preloadedHrefs.add(href);

    const rewrittenHref = rewriteUrl(
      'about:blank', // Preloaded URLs are always absolute.
      href,
    );
    if (rewrittenHref === '') {
      console.warn(`Refused preloading for URL: ${href}`);
      return; // Drop invalid or problematic URLs.
    }
    if (rewrittenHref !== href) {
      if (this._preloadedHrefs.has(rewrittenHref)) {
        return; // Nothing to do.
      }
      this._preloadedHrefs.add(rewrittenHref);
    }

    console.debug(`Preloading with ${priority} priority: ${href}`);
    if (priority === 'low') {
      this._preloadWithLowPriority(rewrittenHref, destination);
    } else {
      this._preloadWithHighPriority(rewrittenHref, destination);
    }
  }

  /**
   * Notify that a resource has been loaded. This will prevent future preloading
   * of the resources, avoiding potential double fetches.
   */
  notifyLoaded(href: string): void {
    this._preloadedHrefs.add(href);
  }

  private _preloadWithLowPriority(href: string, destination: RequestDestination): void {
    if (!this._supportsPrefetch || uaIsChrome) {
      // On browsers where <link rel="prefetch"> is not supported by default
      // (only Safari as of this writing), we used a queued approach as a
      // fallback.
      // TODO(sethf): We're also using this approach temporarily on Chrome to
      // work around a recent change in Chrome behavior: Chrome has started
      // using the value 'empty' for the RequestDestination of <link
      // rel="prefetch">. This is almost certainly a bug (that's not a valid
      // value according to the spec; they probably intended ''). This invalid
      // value prevents the playback service worker from rewriting the resource.
      // I'll make that work in followup PR; it unfortunately implies larger
      // changes to how resource rewriting works. Here's the bug report:
      //   https://bugs.chromium.org/p/chromium/issues/detail?id=1075582
      this._preloadUsingQueuedLinkPreload(href, destination);
      return;
    }

    this._preloadUsingLinkPrefetch(href, destination);
  }

  private _preloadWithHighPriority(href: string, destination: RequestDestination): void {
    if (!this._supportsPreload) {
      // On browsers where <link rel="preload"> is not supported by default
      // (only Firefox as of this writing), we fall back to the lower-priority
      // <link rel="prefetch">.
      this._preloadUsingLinkPrefetch(href, destination);
      return;
    }

    this._preloadUsingLinkPreload(href, destination);
  }

  /**
   * Preload with low priority using <link rel="prefetch">, which fetches with
   * the lowest possible priority. This means that any other fetch can preempt
   * <link rel="prefetch">, so on busy pages with lots of other activity, these
   * resources may never be preloaded at all. (And, indeed, the browser is free
   * to ignore these directives.) See here for additional details:
   *   https://developers.google.com/web/fundamentals/performance/resource-prioritization
   */
  private _preloadUsingLinkPrefetch(href: string, destination: RequestDestination): void {
    this._document.head.appendChild(this._createPreloadElemOfKind('prefetch', href, destination));
  }

  /**
   * Preload with high priority using <link rel="preload">, which fetches with
   * the same priority that would be used if the resource was actually
   * referenced in the page. That means that resources loaded directly via the
   * DOM or CSS can be blocked by <link rel="preload">, so it should be used
   * sparingly. See here for details:
   *   https://developers.google.com/web/fundamentals/performance/resource-prioritization
   */
  private _preloadUsingLinkPreload(href: string, destination: RequestDestination): void {
    this._document.head.appendChild(this._createPreloadElemOfKind('preload', href, destination));
  }

  /**
   * Preload using a queue, loading one resource at a time using
   * <link rel="preload">. This is a fallback strategy for low-priority
   * preloading on browsers where we don't have a better way to do it. Each
   * individual resource is still fetched with high priority, but fetching only
   * one resource at a time minimizes the impact on other resources which may be
   * higher-priority in reality.
   */
  private _preloadUsingQueuedLinkPreload(href: string, destination: RequestDestination): void {
    const previousQueuedPreloadsDone = this._queuedPreloadsDone;
    this._queuedPreloadsDone = (async () => {
      try {
        await previousQueuedPreloadsDone;

        const elem = this._createPreloadElemOfKind('preload', href, destination);
        await loadEvent(elem, () => {
          this._document.head.appendChild(elem);
        });
      } catch (_) {
        // Ignore failures; preloading is best-effort.
      }
    })();
  }

  private _createPreloadElemOfKind(
    rel: 'prefetch' | 'preload',
    href: string,
    destination: RequestDestination,
  ): HTMLLinkElement {
    const preloadElem = this._document.createElement('link');
    preloadElem.rel = rel;
    preloadElem.href = href;
    preloadElem.as = destination;
    return preloadElem;
  }
}
